import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, startTransition } from 'react';
import type { User } from '@supabase/supabase-js';
import { saveBeforeRedirect } from '../utils/attribution';
// Lazy load Supabase to defer 154 KiB bundle until actually needed
// Import only the type at top level to avoid loading the entire SDK
const isBrowser = typeof window !== 'undefined';

// Prefetch prices when user signs in (to avoid loading delay on paywall)
const triggerPricePrefetch = () => {
  import('../utils/pricing/prefetchPrices').then(({ prefetchPrices }) => {
    prefetchPrices().catch(() => {
      // Silently ignore - this is just a performance optimization
    });
  });
};

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signInWithPopup: () => Promise<void>;
  signOut: () => Promise<void>;
}

// Provide a default context value to prevent React error #419 during SSR/hydration
const defaultAuthContext: AuthContextType = {
  user: null,
  isLoading: false,
  signIn: async () => {},
  signInWithPopup: async () => {},
  signOut: async () => {},
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Initialize as false for SSR

  useEffect(() => {
    if (!isBrowser) return;

    // Store subscription reference for cleanup (must be outside the promise chain)
    let authSubscription: { unsubscribe: () => void } | null = null;

    // PERFORMANCE: Defer auth initialization until after page interactive
    // This prevents Supabase from blocking LCP (was causing 3000ms+ delay)
    // Use requestIdleCallback for non-critical auth check, or setTimeout fallback
    const initAuth = () => {
      // HYDRATION FIX: Wrap state updates in startTransition to prevent React Error #421
      startTransition(() => {
        setIsLoading(true);
      });

      // Dynamically import Supabase to defer 154 KiB bundle until auth is actually needed
      // This reduces initial JavaScript bundle size and TBT
      import('../lib/supabase')
        .then(({ supabase }) => {
          // Check active sessions and sets the user
          supabase.auth.getSession()
            .then(({ data: { session } }) => {
              startTransition(() => {
                setUser(session?.user ?? null);
                setIsLoading(false);
              });
              // Prefetch prices if user is already signed in (performance optimization)
              if (session?.user) {
                triggerPricePrefetch();
              }
            })
            .catch((error) => {
              console.error('[AuthProvider] Error getting session:', error);
              startTransition(() => {
                setIsLoading(false);
              });
            });

          // Listen for changes on auth state (signed in, signed out, etc.)
          const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            startTransition(() => {
              setUser(session?.user ?? null);
              setIsLoading(false);
            });
            // Prefetch prices when user signs in (performance optimization)
            if (session?.user) {
              triggerPricePrefetch();
            }
          });

          // Store subscription for cleanup
          authSubscription = subscription;
        })
        .catch((error) => {
          console.error('[AuthProvider] Failed to load Supabase:', error);
          startTransition(() => {
            setIsLoading(false);
          });
        });
    };

    // Defer auth initialization to after page interactive
    // Use 2 second timeout to balance LCP optimization vs auth responsiveness
    // Real users won't notice since auth state shows as loading initially
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initAuth, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(initAuth, 1500);
    }

    // Cleanup: unsubscribe from auth state changes when component unmounts
    return () => {
      authSubscription?.unsubscribe();
    };
  }, []);

  // Memoize signIn to prevent recreating on every render
  const signIn = useCallback(async () => {
    try {
      // Dynamically import Supabase when user actually clicks sign in
      const { supabase } = await import('../lib/supabase');

      // Save attribution state before OAuth redirect
      // This preserves UTM params and referrer across the auth flow
      saveBeforeRedirect();

      // Save current page so we can return after auth
      sessionStorage.setItem('authReturnPath', window.location.pathname);

      // Check if this is an extension auth flow
      const isExtensionAuth = sessionStorage.getItem('extensionAuthFlow') === 'true';
      const redirectPath = isExtensionAuth ? '/auth/extension-callback' : '/auth/callback';

      console.log('[AuthProvider] signIn - extension flow:', isExtensionAuth, 'redirect:', redirectPath);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${redirectPath}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }, []);

  // Popup-based sign in — opens Google OAuth in a popup window
  // Main page stays intact (no redirect), processed images are preserved
  const signInWithPopup = useCallback(async () => {
    const { supabase } = await import('../lib/supabase');

    // Get the OAuth URL without redirecting
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        skipBrowserRedirect: true,
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });

    if (error) throw error;
    if (!data.url) throw new Error('No OAuth URL returned');

    // Open centered popup
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      data.url,
      'google-auth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup) {
      throw new Error('popup_blocked');
    }

    // Wait for the popup to complete auth and send tokens back
    return new Promise<void>((resolve, reject) => {
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'AUTH_POPUP_COMPLETE') return;

        cleanup();

        try {
          // Hydrate auth state in the main window using tokens from the popup
          const { access_token, refresh_token } = event.data;
          await supabase.auth.setSession({ access_token, refresh_token });
          resolve();
        } catch (err) {
          reject(err);
        }
      };

      // Watch for popup being closed without completing auth
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('popup_closed'));
        }
      }, 500);

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
      };

      window.addEventListener('message', handleMessage);
    });
  }, []);

  // Memoize signOut to prevent recreating on every render
  const signOut = useCallback(async () => {
    try {
      // Dynamically import Supabase when user actually clicks sign out
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    user,
    isLoading,
    signIn,
    signInWithPopup,
    signOut,
  }), [user, isLoading, signIn, signInWithPopup, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};